<template>
  <main class="staff-page">
    <header class="page-heading">
      <div>
        <div class="page-eyebrow">Поддержка и лицензии</div>
        <h1>Сроки</h1>
      </div>
      <div class="flex flex-wrap gap-2">
        <Badge variant="destructive">Просрочено: {{ summary.overdue }}</Badge>
        <Badge variant="secondary">В периоде: {{ summary.within_period }}</Badge>
        <Badge v-if="untilLabel" variant="outline">До {{ untilLabel }}</Badge>
        <Badge variant="outline">Всего: {{ items.length }}</Badge>
      </div>
    </header>
    <expirations-filters v-model:model="filters" :products="products" :companies="companies" />
    <div v-if="errorMessage" role="alert" class="ui-alert ui-alert--error mb-4">
      <span>{{ errorMessage }}</span>
      <Button variant="ghost" size="sm" @click="errorMessage = null">Закрыть</Button>
    </div>
    <div v-if="loading" class="ui-empty">
      <Spinner />
      <span>Загрузка сроков…</span>
    </div>
    <template v-else>
      <p v-if="!items.length" class="ui-empty">
        Нет истекающих сроков по выбранным фильтрам.
        <span v-if="filters.mine">Попробуйте «Все».</span>
        <span v-else-if="filters.hideOverdue">
          Снимите «Скрыть просроченные» или выберите «Просрочено».
        </span>
      </p>
      <expiration-card v-else :items="items" />
    </template>
  </main>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import moment from 'moment';
import { useMainStore } from '@/store';
import ExpirationsFilters, {
  ExpirationsFilterModel,
  PeriodMode,
} from '@/components/ExpirationsFilters.vue';
import ExpirationCard from '@/components/ExpirationCard.vue';

const route = useRoute();
const router = useRouter();
const store = useMainStore();

const products = ref<Product[]>([]);
const companies = ref<Company[]>([]);
const items = ref<ExpirationItem[]>([]);
const summary = ref({ overdue: 0, within_period: 0 });
const until = ref<string | null>(null);
const loading = ref(false);
const errorMessage = ref<string | null>(null);

function filtersFromQuery(): ExpirationsFilterModel {
  const q = route.query;
  const onlyOverdue = q.only_overdue === '1';
  const untilDate = typeof q.until_date === 'string' ? q.until_date : null;
  const withinDaysRaw = typeof q.within_days === 'string' ? Number(q.within_days) : null;
  const type = (
    ['all', 'support', 'module'].includes(String(q.type)) ? q.type : 'all'
  ) as ExpirationsFilterModel['type'];
  const productId = q.product ? Number(q.product) : null;
  const supporterId = q.supporter ? Number(q.supporter) : null;
  const mine = q.mine === '1';
  const hideOverdue = q.hide_overdue === '1' || q.include_overdue === '0';

  let periodMode: PeriodMode = '30';
  let withinDays: number | null = 30;

  if (onlyOverdue) {
    periodMode = 'overdue';
    withinDays = null;
  } else if (untilDate) {
    periodMode = 'custom';
    withinDays = null;
  } else if (withinDaysRaw != null && !Number.isNaN(withinDaysRaw)) {
    withinDays = withinDaysRaw;
    if (withinDays === 7 || withinDays === 30 || withinDays === 90) {
      periodMode = String(withinDays) as PeriodMode;
    } else {
      periodMode = 'custom';
    }
  }

  return {
    periodMode,
    withinDays,
    untilDate,
    type,
    productId: productId && !Number.isNaN(productId) ? productId : null,
    supporterId: supporterId && !Number.isNaN(supporterId) ? supporterId : null,
    mine,
    hideOverdue: onlyOverdue ? false : hideOverdue,
  };
}

const filters = ref<ExpirationsFilterModel>(filtersFromQuery());

const untilLabel = computed(() =>
  until.value ? moment(until.value, 'YYYY-MM-DD').format('DD.MM.YYYY') : null
);

function syncQueryFromFilters() {
  const q: Record<string, string> = {};
  const f = filters.value;

  if (f.periodMode === 'overdue') {
    q.only_overdue = '1';
  } else if (f.untilDate) {
    q.until_date = f.untilDate;
  } else {
    q.within_days = String(f.withinDays ?? 30);
  }

  if (f.type !== 'all') q.type = f.type;
  if (f.productId != null) q.product = String(f.productId);
  if (f.supporterId != null) q.supporter = String(f.supporterId);
  if (f.mine) q.mine = '1';
  if (f.hideOverdue && f.periodMode !== 'overdue') q.hide_overdue = '1';

  router.replace({ query: q });
}

async function loadDicts() {
  try {
    const [catalog, companyList] = await Promise.all([
      axios.get('/api/products/catalog/').then((r) => r.data as Product[]),
      store.getCompanies(),
    ]);
    products.value = catalog;
    companies.value = companyList;
  } catch {
    products.value = [];
    companies.value = [];
  }
}

async function loadExpirations() {
  loading.value = true;
  errorMessage.value = null;
  try {
    const f = filters.value;
    const params: Record<string, string | number> = {
      mine: f.mine ? 1 : 0,
      type: f.type,
    };
    if (f.productId != null) params.product = f.productId;
    if (f.supporterId != null) params.supporter = f.supporterId;

    if (f.periodMode === 'overdue') {
      params.only_overdue = 1;
    } else if (f.untilDate) {
      params.until_date = f.untilDate;
      params.hide_overdue = f.hideOverdue ? 1 : 0;
    } else {
      params.within_days = f.withinDays ?? 30;
      params.hide_overdue = f.hideOverdue ? 1 : 0;
    }

    const data = (await axios.get('/api/expirations/', { params })).data as ExpirationsResponse;
    items.value = data.items;
    summary.value = data.summary;
    until.value = data.until;
  } catch (e: any) {
    items.value = [];
    summary.value = { overdue: 0, within_period: 0 };
    until.value = null;
    errorMessage.value = e?.response?.data?.detail || 'Не удалось загрузить сроки';
  } finally {
    loading.value = false;
  }
}

watch(
  filters,
  () => {
    syncQueryFromFilters();
    loadExpirations();
  },
  { deep: true }
);

onMounted(async () => {
  syncQueryFromFilters();
  await loadDicts();
  await loadExpirations();
});
</script>
